import React, { Suspense } from 'react';

import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, type LoaderFunctionArgs, redirect, redirectDocument, RouterProvider } from 'react-router';

import Loader from '@helsenorge/designsystem-react/components/Loader';

import PageRoot from '@helsenorge/core-framework/pageroot';
import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';
import { getSidetittelId } from '@helsenorge/framework-utils/hn-page';
import { getHelsenorgeUrl } from '@helsenorge/framework-utils/hn-proxy-service';

import Forside from './forside-v2';
import { store } from './store';

const AUTOSIGNOUT_PATH = '/autosignout';

const forsideLoader = ({ request }: LoaderFunctionArgs): Response | null => {
  if (request.url.includes('autosignout=1')) {
    return redirect(AUTOSIGNOUT_PATH);
  }

  if (!isAuthorized()) {
    return redirectDocument(getHelsenorgeUrl());
  }

  return null;
};

const router = createBrowserRouter([
  {
    path: '/oidc/signedout',
    lazy: () => import('./utlogget-fra-oidc'),
  },
  {
    path: '/velg-person',
    loader: (): Response | null => {
      if (!isAuthorized()) {
        return redirectDocument('/');
      }

      return null;
    },
    element: (
      <Suspense fallback={<Loader center />}>
        <PageRoot
          noBreadcrumb
          noLanguagePicker
          hasFullwidth
          hasPageHeader={false}
          mainAriaLabelledby={getSidetittelId()}
          enableSurveys={false}
        >
          <Forside />
        </PageRoot>
      </Suspense>
    ),
  },
  {
    path: AUTOSIGNOUT_PATH,
    lazy: () => import('./autosignout'),
  },
  {
    path: '/velg-person?',
    loader: forsideLoader,
    element: (
      <Suspense fallback={<Loader center />}>
        <PageRoot
          noBreadcrumb
          noLanguagePicker
          hasFullwidth
          hasPageHeader={false}
          mainAriaLabelledby={getSidetittelId()}
          enableSurveys={false}
        >
          <Forside />
        </PageRoot>
      </Suspense>
    ),
  },
  {
    path: '*',
    lazy: () => import('./page-not-found'),
  },
]);

async function enableMocking(): Promise<ServiceWorkerRegistration | void> {
  if (import.meta.env.MODE !== 'mocks') {
    return;
  }

  const { worker } = await import('./mocks/browser');

  return worker.start({
    onUnhandledRequest: 'bypass',
  });
}

enableMocking().then(() => {
  ReactDOM.createRoot(document.getElementById('main-content-wrapper')!).render(
    <React.StrictMode>
      <Provider store={store}>
        <RouterProvider router={router} />
      </Provider>
    </React.StrictMode>
  );
});
